import {
  usePartStockPrices,
  useStockItems,
} from '@/core/domain/stock/StockItems/StockItems.hooks';
import {
  PartStockPricesResult,
  StockItemType,
} from '@/core/domain/stock/StockItems/StockItems.types';
import { quantityFormatter } from '@/utils/number';
import { Descriptions, Divider, Image, Space } from 'antd';
import Table, { ColumnsType } from 'antd/lib/table';
import { useIntl } from 'react-intl';
import { Flag } from 'react-feather';
import { getTableScrollX } from '@/utils/table';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import formatterPrice from '@/utils/number/formatterPrice';
import { Group } from '@/components/_common';
import { FaImage } from 'react-icons/fa';
import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';

interface Props {
  stockId: number;
}

export default function SearchPartDetailsStock({ stockId }: Props) {
  const { data, isFetching } = usePartStockPrices(stockId);
  const { data: stockData } = useStockItems(true, {
    ids: [stockId],
    itemType: StockItemType.Part,
  });

  const stockItem = flatInfiniteResult(stockData).at(0);

  const intl = useIntl().formatMessage;
  const translate = (message: string, type = 'column') =>
    intl({ id: `searchPartStock.${type}.${message}` });

  const description = stockItem?.description;
  const manufacturingCode = stockItem?.manufacturingCode;
  const companyCode = stockItem?.companyCode;
  const sku = stockItem?.sku;
  const imageUrl = stockItem?.imageUrl;

  const colorFlag = (value: number) => {
    return (
      <Space>
        {quantityFormatter(value)}
        <Flag color={0 >= value ? 'red' : 'green'} size={12} />
      </Space>
    );
  };

  const columnCostPrice: ColumnsType<PartStockPricesResult> = hasPermissions([
    Authorities.VIEW_PARTS_COSTS,
    Authorities.VIEW_PARTS_PRICES,
  ])
    ? [
        {
          title: translate('costPrice'),
          key: '',
          render: (_, row) => formatterPrice(row.buyPrice),
          width: 100,
        },
      ]
    : [];

  const columnRealCost: ColumnsType<PartStockPricesResult> = hasPermissions([
    Authorities.VIEW_PARTS_COSTS,
  ])
    ? [
        {
          title: translate('realCost'),
          key: 'realCost',
          dataIndex: 'realCost',
          render: (_, row) => formatterPrice(row.realCost),
          width: 100,
        },
      ]
    : [];

  const columnSalePrice: ColumnsType<PartStockPricesResult> = hasPermissions([
    Authorities.VIEW_PARTS_PRICES,
  ])
    ? [
        {
          title: translate('salePrice'),
          key: 'sellPrice',
          dataIndex: 'sellPrice',
          render: (_, row) => `R$ ${quantityFormatter(row.sellPrice)}` || '0',
          width: 100,
        },
      ]
    : [];

  const columns: ColumnsType<PartStockPricesResult> = [
    {
      title: translate('company'),
      key: 'company',
      render: (_, row) => row.company.name,
      ellipsis: true,
      width: 180,
    },
    {
      title: translate('minimumStock'),
      key: 'minStock',
      dataIndex: 'minStock',
      render: (value: number) => quantityFormatter(value) || '0',
      width: 100,
    },
    {
      title: translate('stock'),
      key: 'stock',
      dataIndex: 'stock',
      render: (value: number) => colorFlag(value) || '0',
      width: 90,
    },
    {
      title: translate('availableStock'),
      key: 'availableStock',
      dataIndex: 'availableStock',
      render: (value: number) => colorFlag(value) || '0',
      width: 100,
    },
    {
      title: translate('location'),
      key: 'location',
      dataIndex: 'location',
      render: (value: string) => value || '-',
      width: 140,
    },
    ...columnSalePrice,
    ...columnCostPrice,
    ...columnRealCost,
  ];

  return (
    <Table<PartStockPricesResult>
      columns={columns}
      dataSource={data?.result}
      loading={isFetching}
      rowKey={(row) => row.company.id}
      pagination={false}
      title={() => (
        <>
          <Descriptions
            bordered
            layout="vertical"
            labelStyle={{ fontWeight: 'bold' }}
            size="small"
            column={{ lg: 16, xs: 4, md: 12, sm: 8, xl: 24, xxl: 24 }}
            style={{ marginBottom: 24 }}
          >
            {imageUrl && (
              <Descriptions.Item label={'Imagem'} span={4}>
                <Image
                  width={100}
                  height={60}
                  src={imageUrl}
                  wrapperStyle={{ flexShrink: 0 }}
                  placeholder={
                    <Group position="center" style={{ height: '100%' }}>
                      <FaImage color="lightgray" />
                    </Group>
                  }
                />
              </Descriptions.Item>
            )}

            <Descriptions.Item
              label={translate('title.description')}
              span={imageUrl ? 5 : 6}
            >
              {description ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item
              label={translate('internalCode')}
              span={imageUrl ? 5 : 6}
            >
              {sku ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item
              label={translate('manufacturerCode')}
              span={imageUrl ? 5 : 6}
            >
              {manufacturingCode ?? '-'}
            </Descriptions.Item>

            <Descriptions.Item label={'Código Empresa'} span={imageUrl ? 5 : 6}>
              {companyCode ?? '-'}
            </Descriptions.Item>
          </Descriptions>

          <Divider />
        </>
      )}
      scroll={{ x: getTableScrollX(columns) }}
    />
  );
}
